<template>
  <header class="top-bar" v-if="shouldShowTopBar">
    <div class="top-bar-container">
      <!-- Logo a la izquierda -->
      <div class="top-bar-left">
        <img 
          :src="logoUrl" 
          alt="JLHVISION" 
          class="top-logo" 
          @error="logoUrl = '/favicon.ico'"
        >
      </div>
      
      <!-- Buscador centrado -->
      <div class="top-bar-center">
        <div class="search-container">
          <i class="material-icons search-icon">search</i>
          <input
            type="text"
            v-model="searchQuery"
            @input="debouncedSearch"
            @focus="showResults = true"
            placeholder="Buscar noticias..."
            class="search-input"
          />
          
          <!-- Dropdown de resultados -->
          <div v-if="showResults" class="search-results-dropdown">
            <div v-if="isSearching" class="search-loading">
              <div class="spinner"></div>
              <span>Buscando...</span>
            </div>
            <div v-else-if="searchResults.length === 0 && searchQuery.trim().length >= 2" class="search-no-results">
              No se encontraron resultados para "{{ searchQuery }}"
            </div>
            <div v-else>
              <router-link
                v-for="result in searchResults"
                :key="result.id"
                :to="`/noticia/${result.slug}`"
                class="search-result-item"
                @click="showResults = false"
              >
                <img :src="getImageUrl(result.image)" :alt="result.title" class="result-image">
                <div class="result-info">
                  <span class="result-category">{{ result.category?.name || 'General' }}</span>
                  <h4 class="result-title">{{ result.title }}</h4>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Avatar a la derecha -->
      <div class="top-bar-right">
        <div class="user-avatar" @click="goToProfile">
          <img :src="avatarUrl" :alt="userName" @error="avatarUrl = '/default-avatar.png'">
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const route = useRoute()

const logoUrl = ref('/logo.png')
const avatarUrl = ref('/avatar.png')
const userName = ref('Usuario')

// 🔥 CONTROL DE VISIBILIDAD - SOLO EN ESTAS RUTAS
const shouldShowTopBar = computed(() => {
  const allowedRoutes = ['Home', 'Express', 'Tendencia', 'Radio', 'Mizona', 'categoria']
  return allowedRoutes.includes(route.name)
})

const loadUser = () => {
  const user = localStorage.getItem('user')

  if (user) {
    const userData = JSON.parse(user)
    userName.value = userData.first_name || 'Usuario'
    
    if (userData.avatar) {
      avatarUrl.value = userData.avatar.startsWith('http')
        ? userData.avatar
        : `https://portal.jlhvision.com/avatars/${userData.avatar}`
    } else {
      avatarUrl.value = '/avatar.png'
    }
  }
}

// Estado búsqueda
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)
const showResults = ref(false)
let searchTimeout = null

const getImageUrl = (image) => {
  if (!image) return '/default-image.jpg'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const performSearch = async () => {
  const query = searchQuery.value.trim()

  if (query.length < 2) {
    searchResults.value = []
    showResults.value = false
    return
  }

  isSearching.value = true
  showResults.value = true

  try {
    const response = await api.get('/search', { params: { q: query } })
    searchResults.value = response.data.posts?.data || []
  } catch (error) {
    console.error('Error en la búsqueda:', error)
    searchResults.value = []
  } finally {
    isSearching.value = false
  }
}

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    performSearch()
  }, 500)
}

const handleClickOutside = (event) => {
  const searchContainer = document.querySelector('.search-container')
  if (searchContainer && !searchContainer.contains(event.target)) {
    showResults.value = false
  }
}

onMounted(() => {
  loadUser()
  window.addEventListener('user-updated', loadUser)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('user-updated', loadUser)
  document.removeEventListener('click', handleClickOutside)
  clearTimeout(searchTimeout)
})

const goToProfile = () => {
  router.push('/mizona')
}
</script>

<style scoped>
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  display: block;
  z-index: 1000;
  border: none !important;
  box-shadow: none !important;
}

.top-bar-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
}

.top-bar-left {
  flex: 0 0 auto;
}

.top-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.top-bar-center {
  flex: 1;
  margin: 0 12px;
}

.search-container {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 20px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #ddd;
  border-radius: 30px;
  font-size: 16px;
  background-color: #f1f1f1;
  transition: all 0.2s;
  -webkit-appearance: none;
}

.search-input:focus {
  outline: none;
  border-color: #fff;
  background-color: white;
}

.search-results-dropdown {
  position: absolute;
  top: 52px;
  left: 0;
  right: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 450px;
  overflow-y: auto;
  z-index: 1001;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
}

.search-result-item:active {
  background-color: #f0f0f0;
}

.result-image {
  width: 65px;
  height: 65px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
}

.result-info {
  flex: 1;
}

.result-category {
  font-size: 11px;
  color: #a50000;
  text-transform: uppercase;
  font-weight: bold;
  display: block;
  margin-bottom: 4px;
}

.result-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.search-loading, .search-no-results {
  padding: 24px;
  text-align: center;
  color: #666;
  font-size: 15px;
}

.search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-top-color: #00144a;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.top-bar-right {
  flex: 0 0 auto;
}

.user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
}

.user-avatar:active {
  transform: scale(0.95);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 768px) {
  .top-bar-container {
    padding: 8px 10px;
  }
  
  .top-logo {
    height: 28px;
  }
  
  .top-bar-center {
    margin: 0 8px;
  }
  
  .search-icon {
    left: 12px;
    font-size: 18px;
  }
  
  .search-input {
    padding: 10px 12px 10px 40px;
    font-size: 14px;
  }
  
  .search-results-dropdown {
    top: 46px;
    max-height: 400px;
  }
  
  .result-image {
    width: 55px;
    height: 55px;
  }
  
  .result-title {
    font-size: 14px;
  }
  
  .user-avatar {
    width: 34px;
    height: 34px;
  }
}

@media (max-width: 480px) {
  .top-bar-container {
    padding: 6px 8px;
  }
  
  .top-logo {
    height: 24px;
  }
  
  .search-input {
    padding: 8px 10px 8px 36px;
    font-size: 13px;
  }
  
  .search-icon {
    left: 10px;
    font-size: 16px;
  }
  
  .result-image {
    width: 50px;
    height: 50px;
  }
  
  .result-title {
    font-size: 13px;
  }
  
  .result-category {
    font-size: 10px;
  }
  
  .search-results-dropdown {
    top: 42px;
  }
  
  .user-avatar {
    width: 30px;
    height: 30px;
  }
}
</style>